import { useState } from "react";
import { ActionIcon, Group, Loader, Menu, Stack, Text } from "@mantine/core";
import { IconHistory, IconPlus, IconTrash } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { useTranslation } from "react-i18next";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import { useAssistantStore } from "./useAssistant";
import classes from "./AssistantHistoryMenu.module.css";

export default function AssistantHistoryMenu() {
  const { t } = useTranslation();
  const conversations = useAssistantStore((s) => s.conversations);
  const conversationId = useAssistantStore((s) => s.conversationId);
  const loadConversations = useAssistantStore((s) => s.loadConversations);
  const loadConversation = useAssistantStore((s) => s.loadConversation);
  const clearConversation = useAssistantStore((s) => s.clearConversation);
  const deleteConversation = useAssistantStore((s) => s.deleteConversation);
  const [loading, setLoading] = useState(false);

  const handleOpenChange = async (opened) => {
    if (!opened) return;
    setLoading(true);
    try {
      await loadConversations();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    modals.openConfirmModal({
      title: t("assistant.history.deleteConfirmTitle"),
      children: (
        <Text size="sm">{t("assistant.history.deleteConfirmMessage")}</Text>
      ),
      labels: {
        confirm: t("assistant.history.delete"),
        cancel: t("generic.action_cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: () => deleteConversation(id),
    });
  };

  return (
    <Menu
      position="bottom-end"
      withinPortal
      onChange={handleOpenChange}
      shadow="md"
      width={280}
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          size="sm"
          title={t("assistant.history.menu")}
          aria-label={t("assistant.history.menu")}
        >
          <IconHistory size={14} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconPlus size={14} />}
          onClick={clearConversation}
        >
          {t("assistant.history.newConversation")}
        </Menu.Item>

        <Menu.Divider />
        <Menu.Label>{t("assistant.history.recentConversations")}</Menu.Label>

        {loading && (
          <Group gap="xs" px="sm" py="xs">
            <Loader size={12} />
          </Group>
        )}

        {!loading && conversations.length === 0 && (
          <Text size="xs" c="dimmed" px="sm" py="xs">
            {t("assistant.history.noConversations")}
          </Text>
        )}

        {!loading &&
          conversations.map((c) => (
            <Menu.Item
              key={c.id}
              className={classes.historyItem}
              onClick={() => loadConversation(c.id)}
              rightSection={
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  color="red"
                  className={classes.deleteIcon}
                  onClick={(e) => handleDelete(e, c.id)}
                  aria-label={t("assistant.history.delete")}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              }
            >
              <Stack gap={0}>
                <Text
                  size="sm"
                  lineClamp={1}
                  fw={c.id === conversationId ? 600 : 400}
                >
                  {c.title || t("assistant.history.untitledConversation")}
                </Text>
                <Text size="xs" c="dimmed">
                  <Timeago date={c.updatedAt} />
                </Text>
              </Stack>
            </Menu.Item>
          ))}
      </Menu.Dropdown>
    </Menu>
  );
}
